/**
 * ajax-router compat shim — bridges the surviving IIFE callers
 * (common-cart-controller.fetchUrl, common-products-controller.pjaxUrl,
 * application.scrollToAnchor) to the Phase 8 Turbo router living in
 * core/router.esm.js. Ships an ESM module that sets `window.ajaxRouter`
 * with the same five-method surface.
 *
 * This file exists ONLY because the listed callers are still IIFE; the
 * shim retires in Phase 13 once they migrate. The Phase 8 Turbo router
 * (window.appRouter) does the actual work — `pjaxUrl` and `scrollToTop`
 * are pure forwarders, `fetchUrl` and `scrollToAnchor` retain a tiny
 * native implementation matching the legacy contract.
 *
 * Migrated from theme/js/modules/ajax-router.js (IIFE → ESM, Phase 11).
 *
 * Source path: theme/js/modules/ajax-router.esm.js
 * Built name : ./ajax-router.js (sibling imports must use the built name)
 *
 * AI: Entirely written by AI
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 *
 * [AjaxRouter]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { pjaxUrl as routerPjaxUrl, scrollToTop as routerScrollToTop } from './router.js';
import { scrollTo as animScrollTo } from './core-anim.js';

// Used by common-cart-controller for cart-summary refreshes. Fetches
// the URL, picks the first matching container out of the response, and
// either does the replace itself or hands the parsed fragment to the
// caller via `callback`. The legacy contract: callback === 'replace'
// means "swap the matching element in body for the fetched fragment";
// any other function value gets called with the jQuery-wrapped fragment.
function fetchUrl(url, container, callback) {
  fetch(url, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(response.status + ' ' + response.statusText);
      return response.text();
    })
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector(container);
      if (!fragment) return;

      if (callback === 'replace') {
        const target = document.body.querySelector(container);
        if (target) target.replaceWith(fragment);
      } else if (typeof callback === 'function') {
        callback(fragment);
      }
    })
    .catch((err) => {
      // Match the IIFE's alert() shape so existing UX hasn't changed
      // (the call site swallows errors otherwise).
      alert('error: ' + err.message);
    });
}

function pjaxUrl(url, container, maintainScrollPosition) {
  routerPjaxUrl(url, container, maintainScrollPosition);
}

// Called by application.esm.js:scrollElementIntoView with either a
// hash string ('#header-global') or an Element / jQuery collection.
function scrollToAnchor(hash, callback) {
  let anchor = null;
  if (typeof hash === 'string') {
    anchor = document.querySelector(hash);
  } else if (hash && hash.jquery && hash[0]) {
    anchor = hash[0];
  } else if (hash instanceof Element) {
    anchor = hash;
  }
  if (!anchor) return;

  log('[Router] scrolling to anchor');
  const scrollTop = window.scrollY || window.pageYOffset;
  const windowHeight = window.innerHeight;
  const anchorTop = anchor.getBoundingClientRect().top + scrollTop;
  const htmlHeight = document.documentElement.scrollHeight;
  const scrollDuration = (Math.abs(scrollTop + windowHeight - anchorTop) / htmlHeight) * 1500;
  const headerEl = (() => {
    const $h = appGet('header');
    if ($h && $h[0]) return $h[0];
    return document.querySelector('header');
  })();
  const offset = headerEl ? headerEl.offsetHeight : 0;

  animScrollTo(anchor, {
    offset: -offset,
    duration: Math.max(150, scrollDuration),
    complete: typeof callback === 'function' ? callback : undefined,
  });
}

function scrollToTop() {
  routerScrollToTop();
}

// init() was a no-op in the IIFE; preserve the shape so anything still
// calling .init() (e.g. legacy tooling) doesn't blow up.
function init() {}

window.ajaxRouter = {
  init,
  fetchUrl,
  pjaxUrl,
  scrollToAnchor,
  scrollToTop,
};

log('[AjaxRouter] registered');
